import { Component, HostBinding, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { AppState } from 'app/app.state';
import { NotificationType } from 'app/shared/models/notification-type';
import { NotificationService } from 'app/shared/services/notification/notification.service';
import { UserService } from 'app/shared/services/user/user.service';
import { APP_CONFIG } from 'config/app.config';
import { environment } from 'environments/environment';
import { EMPTY, filter, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { IChangePasswordFormInterface } from './models/change-password-form.interface';
import { ChangePasswordService } from './services/change-password.service';

@Component({
    selector: 'upsc-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent implements OnDestroy {
    @HostBinding('class') public hostClass = 'change-password';

    public formGroup: FormGroup<IChangePasswordFormInterface>;
    public isFormSubmitting = false;
    protected readonly APP_CONFIG = APP_CONFIG;
    private ngDestroy$ = new Subject<void>();

    public constructor(private readonly formBuilder: FormBuilder,
                       private readonly translateService: TranslateService,
                       private readonly changePasswordService: ChangePasswordService,
                       private readonly userService: UserService,
                       private readonly notificationService: NotificationService,
                       private readonly appState: AppState,
    ) {
        this.initForm();
        this.translateService.onLangChange.subscribe(() => {
            this.updateForm();
        });
    }

    public ngOnDestroy(): void {
        this.ngDestroy$.next();
        this.ngDestroy$.complete();
    }

    public onFormSubmit(event: SubmitEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (environment.feature.coreAuth) {
            this.secureUpdatePassword();
            return;
        }

        this.updatePassword();
    }

    private secureUpdatePassword(): void {
        const currentPassword = this.formGroup.controls.currentPassword.value;
        const newPassword = this.formGroup.controls.newPassword.value;

        this.isFormSubmitting = true;
        this.changePasswordService.secureUpdatePassword(currentPassword, newPassword)
            .pipe(
                takeUntil(this.ngDestroy$),
                catchError((err) => {
                    this.notificationService.notify(
                        err?.message,
                        'Error',
                        NotificationType.ERROR,
                    );
                    return of(null);
                }),
                finalize(() => {
                    this.isFormSubmitting = false;
                }),
            )
            .subscribe(
                (res: { message: string }) => {
                    if (!res?.message) {
                        this.notificationService.notify(
                            'Error updating password. Please try again.',
                            'Error',
                            NotificationType.ERROR,
                        );

                        return;
                    }

                    this.notificationService.notify(
                        res?.message,
                        'Success',
                        NotificationType.SUCCESS,
                    );

                    this.resetForm();
                },
            );
    }

    private updatePassword(): void {
        const username = this.appState.user$().UserName;

        this.isFormSubmitting = true;

        const currentPassword = this.formGroup.controls.currentPassword.value;
        const newPassword = this.formGroup.controls.newPassword.value;

        this.changePasswordService.validatePassword(username, currentPassword)
            .pipe(
                takeUntil(this.ngDestroy$),
                catchError((err) => {
                    this.notificationService.notify(
                        err.message,
                        'Error',
                        NotificationType.ERROR,
                    );
                    return of(null);
                }),
                tap((isValid) => {
                    if (isValid === false) {
                        this.notificationService.notify(
                            'Error validating password. Please try again.',
                            'Error',
                            NotificationType.ERROR,
                        );
                    }
                }),
                filter(isValid => !!isValid),
                switchMap(() => this.changePasswordService.updatePassword(username, newPassword)),
                finalize(() => this.isFormSubmitting = false),
            ).subscribe((isSuccess) => {
            if (!isSuccess) {
                this.notificationService.notify(
                    'Error updating password. Please try again.',
                    'Error',
                    NotificationType.ERROR,
                );

                return;
            }

            this.notificationService.notify(
                'Password has been updated.',
                'Success',
                NotificationType.SUCCESS,
            );

            this.resetForm();
        });
    }

    private updateForm(): void {
        const passwordRequiredText = this.translateService.instant('passwordValidation.passwordRequired');
        this.formGroup = this.formBuilder.group<IChangePasswordFormInterface>({
            currentPassword: new FormControl<string>(null, RxwebValidators.required({ message: passwordRequiredText })),
            newPassword: new FormControl<string>(null, [
                RxwebValidators.required({ message: passwordRequiredText }),
                RxwebValidators.password({
                    validation: {
                        minLength: APP_CONFIG.minPasswordLength,
                        digit: true,
                        alphabet: true,
                    },
                    message: {
                        minLength: this.translateService.instant('passwordValidation.minLength', { minPasswordLength: APP_CONFIG.minPasswordLength }),
                        digit: this.translateService.instant('passwordValidation.digit', { minDigitCount: 1 }),
                        alphabet: this.translateService.instant('passwordValidation.alphabet', { minAlphabetCount: 1 }),
                    },
                }),
            ]),
            confirmNewPassword: new FormControl<string>(null, [
                RxwebValidators.compare({
                    fieldName: 'newPassword',
                    message: this.translateService.instant('passwordValidation.passwordNotMatched'),
                }),
            ]),
        });
    }

    private initForm(): void {
        this.updateForm();
    }

    private resetForm(): void {
        this.formGroup.reset({}, { emitEvent: false });
        this.formGroup.markAsPristine();
        this.formGroup.markAsUntouched();
        Object.entries(this.formGroup.controls).forEach(([key, value]) => {
            (value as AbstractControl).setErrors(null);
        });
    }
}
